import { useAdaptiveLayout } from '@/hooks/useAdaptiveLayout';
import { useClientTranslations } from '@/hooks/useClientTranslations';
import { parseText } from '@/utils/parseText';
import cn from 'classnames';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { ArrowIcon } from '../Icons';
import styles from './Breadcrumbs.module.scss';

export enum BreadcrumbsVariant {
  Dark = 'dark',
  Light = 'light',
}

type BreadcrumbsProps = {
  routes: { link: string; text: string }[];
  className?: string;
  variant?: BreadcrumbsVariant;
};

export function Breadcrumbs({
  routes,
  className,
  variant = BreadcrumbsVariant.Dark,
}: BreadcrumbsProps) {
  const router = useRouter();
  const paginationClassName = cn(styles.pagination, className);
  const { isTablet } = useAdaptiveLayout();
  const lightVariant = variant === BreadcrumbsVariant.Light && !isTablet;
  const t = useClientTranslations();

  const getIsCurrentPage = (link: string) => {
    return (
      router.asPath === link ||
      `${router.asPath}/` === link ||
      router.asPath.split('?')[0] === link
    );
  };

  const handleRouteClick = (link: string) => {
    if (getIsCurrentPage(link)) {
      return;
    }

    if (Object.values(router.query)[0] === link) {
      return;
    }

    router.push(link);
  };

  return (
    <div className={paginationClassName}>
      <Link
        className={cn(styles.route, {
          [styles.light]: lightVariant,
        })}
        href="/"
      >
        {t.breadcrumbs.home}
      </Link>
      {routes.map((route) => (
        <div
          key={route.link}
          className={cn(styles.routeNext, {
            [styles.nonClick]: getIsCurrentPage(route.link),
            [styles.light]: lightVariant,
          })}
        >
          <ArrowIcon
            color={lightVariant ? 'white' : 'black'}
            className={styles.arrow}
            size={isTablet ? 12 : 24}
          />
          <span
            onClick={() => handleRouteClick(route.link)}
            className={cn(styles.route, {
              [styles.light]: lightVariant,
            })}
          >
            {parseText(route.text)}
          </span>
        </div>
      ))}
    </div>
  );
}
